import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  FormControl,
  FormLabel,
  VStack,
  Badge,
  Spinner,
  HStack,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import TableActionsMenu from 'components/menu/CompanyTableMenu';
import * as React from 'react';
import {
  MdEdit,
  MdDelete,
  MdPerson,
  MdLocationOn,
  MdPhone,
  MdHome,
  MdShoppingCart,
} from 'react-icons/md';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import {
  collection,
  getDocs,
  getFirestore,
  query,
  orderBy,
  limit,
  startAfter,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getCountFromServer,
} from 'firebase/firestore';
import app from '../../../../config/firebaseConfig';

// Initialize Firebase app
const db = getFirestore(app);

const columnHelper = createColumnHelper();

export default function UserTable(props) {
  // Data states
  const [data, setData] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [sorting, setSorting] = React.useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(1);
  const [allDocs, setAllDocs] = React.useState([]); // Store all documents for pagination

  // UI states
  const [loading, setLoading] = React.useState(false);
  const [productsLoading, setProductsLoading] = React.useState(false);

  // Modal states
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Form states
  const [editingRow, setEditingRow] = React.useState(null);
  const [formData, setFormData] = React.useState({
    addresses: {
      home: {
        name: '',
        phoneNumber: '',
        addressType: 'Home',
        address: '',
        city: '',
        state: '',
        pincode: '',
        fullAddress: '',
      }
    }
  });

  const toast = useToast();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const brandColor = useColorModeValue('brand.500', 'brand.400');

  // Function to get total count of users
  const getTotalUserCount = async () => {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getCountFromServer(usersRef);
      return snapshot.data().count;
    } catch (error) {
      console.error('Error getting user count:', error);
      toast({
        title: 'Error',
        description: 'Failed to get user count',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return 0;
    }
  };

  // Function to get all users for pagination
  const getAllUsersForPagination = async () => {
    try {
      const usersRef = collection(db, 'users');
      const usersQuery = query(usersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(usersQuery);
      
      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAllDocs(users);
      return users;
    } catch (error) {
      console.error('Error getting all users:', error);
      toast({
        title: 'Error',
        description: 'Failed to get all users',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return [];
    }
  };

  // Fetch users data with pagination
  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Calculate start position based on current page and page size
      // Similar to your MongoDB example: const start = pageNo * countVal - countVal;
      const start = currentPage * pageSize - pageSize;
      
      // Get total count of users
      const total = await getTotalUserCount();
      setTotalCount(total);
      setTotalPages(Math.ceil(total / pageSize));
      
      // Get all users if not already loaded
      let users = allDocs;
      if (allDocs.length === 0) {
        users = await getAllUsersForPagination();
      }
      
      // Get users for the current page
      // Similar to your MongoDB example: query.skip(start).limit(countVal)
      const paginatedUsers = users.slice(start, start + pageSize);
      setData(paginatedUsers);
    } catch (error) {
      toast({
        title: 'Error fetching data',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial data
  React.useEffect(() => {
    fetchUsers();
  }, []);

  // Re-fetch data when page or pageSize changes
  React.useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize]);

  // Handle Edit
  const handleEdit = (row) => {
    setEditingRow(row);
    setFormData({
      addresses: {
        home: {
          name: row.addresses?.home?.name || '',
          phoneNumber: row.addresses?.home?.phoneNumber || '',
          addressType: row.addresses?.home?.addressType || 'Home',
          address: row.addresses?.home?.address || '',
          city: row.addresses?.home?.city || '',
          state: row.addresses?.home?.state || '',
          pincode: row.addresses?.home?.pincode || '',
          fullAddress: row.addresses?.home?.fullAddress || '',
        }
      }
    });
    onOpen();
  };

  // Handle Delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteDoc(doc(db, 'users', id));
        toast({
          title: 'Success',
          description: 'Customer deleted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        // Reset allDocs to force refresh
        setAllDocs([]);
        fetchUsers();
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  // Handle Submit (Create/Update)
  const handleSubmit = async () => {
    try {
      if (editingRow) {
        // Update existing user
        await updateDoc(doc(db, 'users', editingRow.id), {
          ...formData,
          createdAt: new Date()
        });
        toast({
          title: 'Success',
          description: 'Customer updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new user
        await addDoc(collection(db, 'users'), {
          ...formData,
          createdAt: new Date()
        });
        toast({
          title: 'Success',
          description: 'Customer created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      onClose();
      // Reset allDocs to force refresh
      setAllDocs([]);
      fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(Number(newPageSize));
    setCurrentPage(1);
  };

  // Handle page navigation
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Table columns
  const columns = [
    columnHelper.accessor('addresses.home.name', {
      id: 'addresses.home.name',
      header: () => (
        <Flex align="center">
          <Icon as={MdPerson} mr={2} />
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            Name
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('addresses.home.phoneNumber', {
      id: 'addresses.home.phoneNumber',
      header: () => (
        <Flex align="center">
          <Icon as={MdPhone} mr={2} />
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            Mobile
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('addresses.home.address', {
      id: 'addresses.home.address',
      header: () => (
        <Flex align="center">
          <Icon as={MdLocationOn} mr={2} />
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            Address
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="600" noOfLines={2}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('addresses.home.addressType', {
      id: 'addresses.home.addressType',
      header: () => (
        <Flex align="center">
          <Icon as={MdHome} mr={2} />
          <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
            Address Type
          </Text>
        </Flex>
      ),
      cell: (info) => (
        <Badge colorScheme="blue" fontSize="xs">
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor('addresses.home.city', {
      id: 'addresses.home.city',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          City
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="600">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('addresses.home.state', {
      id: 'addresses.home.state',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          State
        </Text>
      ),
      cell: (info) => (
        <Badge colorScheme="purple" fontSize="xs">
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor('addresses.home.pincode', {
      id: 'addresses.home.pincode',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Pincode
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="600">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Last Updated
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="600">
          {formatTimestamp(info.getValue())}
        </Text>
      ),
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => (
        <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
          Actions
        </Text>
      ),
      cell: (info) => (
        <TableActionsMenu
          row={info.row.original}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ),
    })
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  return (
    <>
      <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
        {/* Header */}
        <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
          <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
            Customer Table
          </Text>
          {/* <HStack spacing={2}>
            <Button
              leftIcon={<MdShoppingCart />}
              colorScheme="green"
              variant="solid"
              onClick={fetchProductsData}
              isLoading={productsLoading}
              size="sm"
            >
              Fetch Products
            </Button>
          </HStack> */}
        </Flex>

        {/* Table */}
        <Box>
          <Table variant="simple" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      pe="10px"
                      borderColor={borderColor}
                      cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Flex justifyContent="space-between" align="center">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={columns.length} textAlign="center">
                    <Spinner size="lg" />
                  </Td>
                </Tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <Tr>
                  <Td colSpan={columns.length} textAlign="center">
                    <Text>No data available</Text>
                  </Td>
                </Tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        key={cell.id}
                        fontSize={{ sm: '14px' }}
                        minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                        borderColor="transparent"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination */}
        <Flex
          px="25px"
          pb="20px"
          justifyContent="space-between"
          align="center"
          flexWrap="wrap"
          gap="3"
        >
          <Flex align="center" gap="2">
            <Text fontSize="sm" color={textColor}>Show</Text>
            <Select
              size="sm"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              w="70px"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Select>
            <Text fontSize="sm" color={textColor}>entries</Text>
          </Flex>

          <Flex align="center" gap="2">
            <Text color={textColor} fontSize="16px" fontWeight="500">
              Total : {totalCount}
            </Text>

            <IconButton
              aria-label="Previous page"
              icon={<IoChevronBack />}
              size="sm"
              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
              isDisabled={currentPage === 1}
            />

            <Text fontSize="sm" color={textColor}>
              Page {currentPage} of {totalPages}
            </Text>

            <IconButton
              aria-label="Next page"
              icon={<IoChevronForward />}
              size="sm"
              onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
              isDisabled={currentPage === totalPages}
            />
          </Flex>
        </Flex>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent display="flex" flexDirection="column" maxH="90vh">
          <ModalHeader>{editingRow ? 'Edit Customer' : 'Create Customer'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" flex="1">
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  value={formData.addresses.home.name}
                  onChange={(e) => setFormData({
                    ...formData,
                    addresses: {
                      ...formData.addresses,
                      home: {
                        ...formData.addresses.home,
                        name: e.target.value
                      }
                    }
                  })}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Mobile</FormLabel>
                <Input
                  value={formData.addresses.home.phoneNumber}
                  isReadOnly={editingRow !== null} // Make it read-only when editing
                  bg={editingRow !== null ? "gray.100" : "white"} // Visual indicator
                  cursor={editingRow !== null ? "not-allowed" : "text"}
                  onChange={(e) => setFormData({
                    ...formData,
                    addresses: {
                      ...formData.addresses,
                      home: {
                        ...formData.addresses.home,
                        phoneNumber: e.target.value
                      }
                    }
                  })}
                />
                {editingRow !== null && (
                  <Text fontSize="xs" color="gray.500" mt="1">
                    Mobile number cannot be edited
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Address Type</FormLabel>
                <Select
                  value={formData.addresses.home.addressType}
                  onChange={(e) => setFormData({
                    ...formData,
                    addresses: {
                      ...formData.addresses,
                      home: {
                        ...formData.addresses.home,
                        addressType: e.target.value
                      }
                    }
                  })}
                >
                  <option value="Home">Home</option>
                  <option value="Work">Work</option>
                  <option value="Other">Other</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Address</FormLabel>
                <Input
                  value={formData.addresses.home.address}
                  onChange={(e) => setFormData({
                    ...formData,
                    addresses: {
                      ...formData.addresses,
                      home: {
                        ...formData.addresses.home,
                        address: e.target.value
                      }
                    }
                  })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>City</FormLabel>
                <Input
                  value={formData.addresses.home.city}
                  onChange={(e) => setFormData({
                    ...formData,
                    addresses: {
                      ...formData.addresses,
                      home: {
                        ...formData.addresses.home,
                        city: e.target.value
                      }
                    }
                  })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>State</FormLabel>
                <Input
                  value={formData.addresses.home.state}
                  onChange={(e) => setFormData({
                    ...formData,
                    addresses: {
                      ...formData.addresses,
                      home: {
                        ...formData.addresses.home,
                        state: e.target.value
                      }
                    }
                  })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Pincode</FormLabel>
                <Input
                  value={formData.addresses.home.pincode}
                  onChange={(e) => setFormData({
                    ...formData,
                    addresses: {
                      ...formData.addresses,
                      home: {
                        ...formData.addresses.home,
                        pincode: e.target.value
                      }
                    }
                  })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Full Address</FormLabel>
                <Input
                  value={formData.addresses.home.fullAddress}
                  onChange={(e) => setFormData({
                    ...formData,
                    addresses: {
                      ...formData.addresses,
                      home: {
                        ...formData.addresses.home,
                        fullAddress: e.target.value
                      }
                    }
                  })}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="brand" onClick={handleSubmit}>
              {editingRow ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}